import { buildGatsbyAssetReference } from './asset'
import { isContentfulLink, isOverlayableText, isRecord, UnknownRecord } from './shared'
import { ContentfulPreviewAsset, ContentfulPreviewEntry } from '../../services/contentfulPreviewService'

/**
 * Builds Gatsby-shaped `references` entries for the entry types that can be embedded in blog rich
 * text, so an embed added after build renders without waiting for a re-source.
 *
 * Unlike images, this cannot be derived — each content type has its own fragment, so the mapping
 * from Content Preview API fields to the Gatsby shape has to be stated. What keeps that honest is
 * that CPA field ids and Gatsby field names are identical here (verified against the Contentful
 * content model), so a spec only has to say what *kind* each field is, not rename
 * anything.
 *
 * Scope is deliberately the three types Contentful's "add existing entry" dropdown offers on a blog
 * post — Generic Table, OneBlock, OneLink — plus what they link to. Anything not in `FIELD_SPECS`
 * is left on build-time data rather than half-built.
 */

type FieldKind = 'text' | 'longText' | 'stringList' | 'boolean' | 'entry' | 'entryList' | 'asset'

/**
 * Each spec mirrors the GraphQL fragment for that type, field for field. A field the fragment
 * selects but the spec omits would be missing from the built reference and could break the
 * component, so keep these in step with `src/graphql/**` — `entryReference.ts` tests assert the
 * shape matches what the fragments declare.
 *
 * `longText` is the one real shape difference: Contentful sends a string, Gatsby wraps it as
 * `{ <fieldName>: string }`. Note `alternateText` is long text on ContentfulImage and short text on
 * ContentfulLink — same field name, different kind, which is why this is per type.
 */
const FIELD_SPECS: Record<string, Record<string, FieldKind>> = {
  // src/graphql/modules/contentfulGenericTable.ts
  ContentfulGenericTable: {
    title: 'text',
    displayTitle: 'text',
    tableHead: 'entry',
    tableRows: 'entryList',
    seperator: 'text',
  },
  ContentfulGenericTableRow: {
    cells: 'stringList',
  },
  // src/graphql/sections/oneBlock.ts
  ContentfulOneBlock: {
    title: 'text',
    oneBlock: 'entry',
    showAppStoreDownloadButtons: 'boolean',
    backgroundImageDesktop: 'entry',
    backgroundImageTablet: 'entry',
    backgroundImageMobile: 'entry',
  },
  // src/graphql/sections/oneLink.ts
  ContentfulOneLink: {
    title: 'text',
    oneLink: 'entry',
    backgroundImageDesktop: 'entry',
    backgroundImageTablet: 'entry',
    backgroundImageMobile: 'entry',
  },
  // src/graphql/modules/copy.ts
  ContentfulCopy: {
    title: 'text',
    text: 'longText',
    image: 'entry',
    link: 'entry',
  },
  // src/graphql/modules/link.ts
  ContentfulLink: {
    image: 'entry',
    alternateText: 'text',
    link: 'text',
    linkName: 'text',
    linkText: 'text',
    callToAction: 'boolean',
    cookieValue: 'text',
    cookieKey: 'text',
    rel: 'text',
  },
  // src/graphql/modules/image.ts
  ContentfulImage: {
    title: 'text',
    description: 'longText',
    alternateText: 'longText',
    image: 'asset',
  },
}

/** The entry types this can build, i.e. what may be embedded and still render live. */
export const SUPPORTED_EMBED_TYPENAMES = Object.keys(FIELD_SPECS)

/**
 * `gatsby-source-contentful`'s own naming: `makeTypeName = upperFirst(camelCase('Contentful ' + id))`
 * (`gatsby-source-contentful/normalize.js`). Reimplemented rather than pulling in lodash, because
 * this module ships in the production bundle and content type ids are plain identifiers.
 */
export const typeNameForContentType = (contentTypeId: string): string => {
  const camel = contentTypeId
    .replace(/[-_\s]+(.)?/g, (_match, char) => (char ? char.toUpperCase() : ''))
    .replace(/^(.)/, char => char.toUpperCase())

  return `Contentful${camel}`
}

const entryTypeName = (entry: ContentfulPreviewEntry): string | undefined => {
  const contentType = entry.sys.contentType
  if (!contentType || !contentType.sys || typeof contentType.sys.id !== 'string') return undefined
  return typeNameForContentType(contentType.sys.id)
}

const linkedId = (value: unknown, linkType: 'Entry' | 'Asset'): string | undefined => {
  if (isContentfulLink(value, linkType)) return value.sys.id

  // Live preview sometimes merges the target in resolved rather than as a Link.
  if (isRecord(value) && isRecord(value.sys) && value.sys.type === linkType && typeof value.sys.id === 'string') {
    return value.sys.id
  }

  return undefined
}

/** Cycles are possible through entry links; blog embeds nest three or four deep at most. */
const MAX_DEPTH = 8

type BuildContext = {
  entriesById: Map<string, ContentfulPreviewEntry>
  assetsById: Map<string, ContentfulPreviewAsset>
  visiting: Set<string>
  depth: number
}

const buildField = (kind: FieldKind, value: unknown, fieldName: string, context: BuildContext): unknown => {
  switch (kind) {
    case 'text':
      return isOverlayableText(value) ? value : undefined

    case 'longText':
      // Contentful sends a string; Gatsby wraps it as `{ <fieldName>: string }`.
      return isOverlayableText(value) ? { [fieldName]: value } : undefined

    case 'stringList':
      return Array.isArray(value) && value.every(item => typeof item === 'string') ? value : undefined

    case 'boolean':
      return typeof value === 'boolean' ? value : undefined

    case 'asset': {
      const assetId = linkedId(value, 'Asset')
      if (!assetId) return undefined
      const asset = context.assetsById.get(assetId)
      return asset ? buildGatsbyAssetReference(asset) : undefined
    }

    case 'entry': {
      const entryId = linkedId(value, 'Entry')
      return entryId ? buildReference(entryId, context) : undefined
    }

    case 'entryList': {
      if (!Array.isArray(value)) return undefined
      const built = value
        .map(item => {
          const entryId = linkedId(item, 'Entry')
          return entryId ? buildReference(entryId, context) : undefined
        })
        .filter(Boolean)
      return built.length ? built : undefined
    }

    default:
      return undefined
  }
}

function buildReference(entryId: string, context: BuildContext): UnknownRecord | undefined {
  if (context.depth > MAX_DEPTH || context.visiting.has(entryId)) return undefined

  const entry = context.entriesById.get(entryId)
  if (!entry || !isRecord(entry.fields)) return undefined

  const typename = entryTypeName(entry)
  if (!typename) return undefined

  const spec = FIELD_SPECS[typename]
  if (!spec) return undefined

  const nested: BuildContext = {
    ...context,
    depth: context.depth + 1,
    visiting: new Set([...context.visiting, entryId]),
  }

  const reference: UnknownRecord = { __typename: typename, contentful_id: entryId }

  for (const [fieldName, kind] of Object.entries(spec)) {
    const built = buildField(kind, entry.fields[fieldName], fieldName, nested)
    if (built !== undefined) reference[fieldName] = built
  }

  return reference
}

/**
 * A Gatsby `references` entry for one embedded Contentful entry, or undefined when it cannot be
 * built completely enough to render — an unsupported type, or a linked entry the preview payload
 * does not have yet. Undefined means "keep build-time data", never "render a partial entry".
 */
export const buildGatsbyEntryReference = (
  entryId: string,
  entriesById: Map<string, ContentfulPreviewEntry>,
  assetsById: Map<string, ContentfulPreviewAsset>,
): UnknownRecord | undefined => buildReference(entryId, { entriesById, assetsById, visiting: new Set(), depth: 0 })
